import { useState } from 'react';
import { Link } from 'react-router-dom';

type Gender = 'male' | 'female' | 'other' | 'prefer_not';
type ChatMode = 'video' | 'text' | 'both';

const genderOptions: { value: Gender; label: string; icon: string }[] = [
  { value: 'male', label: 'Erkek', icon: 'ri-men-line' },
  { value: 'female', label: 'Kadın', icon: 'ri-women-line' },
  { value: 'other', label: 'Diğer', icon: 'ri-genderless-line' },
  { value: 'prefer_not', label: 'Belirtmek İstemiyorum', icon: 'ri-eye-off-line' },
];

const chatModeOptions: { value: ChatMode; label: string; icon: string; desc: string }[] = [
  { value: 'video', label: 'Görüntülü', icon: 'ri-video-on-line', desc: 'Yalnızca görüntülü sohbet' },
  { value: 'text', label: 'Metin', icon: 'ri-chat-3-line', desc: 'Yalnızca yazılı sohbet' },
  { value: 'both', label: 'Her İkisi', icon: 'ri-layout-grid-line', desc: 'Görüntülü ve metin' },
];

const PreferencesSection = () => {
  const [gender, setGender] = useState<Gender>('prefer_not');
  const [preferredGender, setPreferredGender] = useState<Gender[]>(['male', 'female', 'other']);
  const [chatMode, setChatMode] = useState<ChatMode>('both');
  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(40);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isPremium = false;

  const togglePreferredGender = (g: Gender) => {
    setPreferredGender((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 900);
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-xl font-extrabold text-white mb-1">Tercihler</h2>
        <p className="text-white/40 text-sm">Eşleşme ve sohbet tercihlerini ayarla.</p>
      </div>

      <div className="flex flex-col gap-6">
        {/* Gender */}
        <div className="p-6 rounded-2xl bg-white/3 border border-white/8">
          <label className="block text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">
            Cinsiyetim
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {genderOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGender(opt.value)}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl border text-sm transition-all cursor-pointer ${
                  gender === opt.value
                    ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400'
                    : 'bg-white/3 border-white/10 text-white/50 hover:bg-white/6 hover:text-white/70'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${gender === opt.value ? 'bg-cyan-500/20' : 'bg-white/5'}`}>
                  <i className={`${opt.icon} text-lg`} />
                </div>
                <span className="text-xs font-medium text-center leading-tight whitespace-nowrap">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Preferred gender — premium locked */}
        <div className="p-6 rounded-2xl bg-white/3 border border-white/8 relative">
          {!isPremium && (
            <div className="absolute inset-0 rounded-2xl bg-black/40 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/15 border border-yellow-500/30">
                <i className="ri-vip-crown-line text-yellow-400 text-sm" />
                <span className="text-yellow-400 text-sm font-bold whitespace-nowrap">Premium Özelliği</span>
              </div>
              <p className="text-white/50 text-xs text-center max-w-xs">Tercih ettiğin cinsiyet filtresini açmak için Premium'a geç.</p>
              <Link
                to="/premium"
                className="px-5 py-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-extrabold cursor-pointer whitespace-nowrap"
              >
                Premium'a Geç
              </Link>
            </div>
          )}
          <label className="block text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">
            Tercih Ettiğim Cinsiyet
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {genderOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => isPremium && togglePreferredGender(opt.value)}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl border text-sm transition-all cursor-pointer ${
                  preferredGender.includes(opt.value)
                    ? 'bg-violet-500/15 border-violet-500/40 text-violet-400'
                    : 'bg-white/3 border-white/10 text-white/50 hover:bg-white/6 hover:text-white/70'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${preferredGender.includes(opt.value) ? 'bg-violet-500/20' : 'bg-white/5'}`}>
                  <i className={`${opt.icon} text-lg`} />
                </div>
                <span className="text-xs font-medium text-center leading-tight whitespace-nowrap">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Chat mode */}
        <div className="p-6 rounded-2xl bg-white/3 border border-white/8">
          <label className="block text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">
            Sohbet Modu
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {chatModeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setChatMode(opt.value)}
                className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all cursor-pointer ${
                  chatMode === opt.value
                    ? 'bg-cyan-500/12 border-cyan-500/35 text-white'
                    : 'bg-white/3 border-white/10 text-white/60 hover:bg-white/5 hover:text-white/75'
                }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${chatMode === opt.value ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/6 text-white/35'}`}>
                  <i className={`${opt.icon} text-lg`} />
                </div>
                <div>
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-white/40 mt-0.5">{opt.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Age range */}
        <div className="p-6 rounded-2xl bg-white/3 border border-white/8">
          <div className="flex items-center justify-between mb-4">
            <label className="text-white/50 text-xs font-semibold uppercase tracking-widest">
              Yaş Aralığı Tercihi
            </label>
            <span className="text-cyan-400 text-sm font-bold">{minAge} – {maxAge}</span>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/40 text-xs">Minimum</span>
                <span className="text-white/60 text-xs font-mono">{minAge}</span>
              </div>
              <input
                type="range"
                min={18}
                max={maxAge - 1}
                value={minAge}
                onChange={(e) => setMinAge(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 accent-cyan-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/40 text-xs">Maksimum</span>
                <span className="text-white/60 text-xs font-mono">{maxAge}</span>
              </div>
              <input
                type="range"
                min={minAge + 1}
                max={65}
                value={maxAge}
                onChange={(e) => setMaxAge(Number(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-white/10 accent-cyan-500"
              />
            </div>
          </div>
          <p className="text-white/25 text-xs mt-3">Kesin eşleşme garantisi verilmez, yalnızca tercih olarak işlenir.</p>
        </div>

        {/* Save */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3.5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-sm hover:from-cyan-400 hover:to-violet-500 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap shadow-lg shadow-cyan-500/20"
          >
            {saving ? (
              <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" /> Kaydediliyor...</span>
            ) : saved ? (
              <span className="flex items-center gap-2 text-emerald-300"><i className="ri-checkbox-circle-line" /> Kaydedildi!</span>
            ) : (
              <span className="flex items-center gap-2"><i className="ri-save-line" /> Kaydet</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;
